import { Anchor, Box, Flex, Progress, Text, Tooltip } from '@mantine/core';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { calculateDaysLeft, formatDate } from 'utils/dates';
import { Enlistment } from 'models/user';
import { GroupSettingsTabs } from 'pages/Settings/Forms/GroupSettings.helpers';
import { useNavigate } from 'react-router-dom';

interface GroupProgressBarItemProps {
  group: Enlistment;
  w?: number;
}

export const GroupProgressBarItem = ({ group, w }: GroupProgressBarItemProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const calculateProgress = (creationDate?: Date, expirationDate?: Date, hasExpired = false) => {
    if (hasExpired) return 100;

    const nowUnix = dayjs(new Date()).unix();
    const creationDateUnix = dayjs(creationDate).unix();
    const expirationDateUnix = dayjs(expirationDate).unix();
    const progressValue = ((nowUnix - creationDateUnix) / (expirationDateUnix - creationDateUnix)) * 100;

    return Math.min(100, Math.max(0, progressValue));
  };

  const showDaysLeft = (expirationDate: Date, hasExpired = false) => {
    if (hasExpired) return t('patients_header_expired');

    const numberOfDays = calculateDaysLeft(expirationDate);

    if (numberOfDays > 1) {
      return `${numberOfDays} ${t('patients_header_days_remaining')}`;
    }
    if (numberOfDays <= 1) {
      return `1 ${t('patients_header_day_remaining')}`;
    }
    return t('patients_header_expired');
  };

  return (
    <Box>
      <Anchor
        size={'xs'}
        fw={'bold'}
        c={'var(--mantine-color-turquoise-7)'}
        onClick={() => navigate(`/settings/${group.groupId}/${GroupSettingsTabs.General}`)}
      >
        {group.name}
      </Anchor>
      <Tooltip label={showDaysLeft(group.expiryTimestamp, group.expired)} position={'bottom'}>
        <Progress
          w={w}
          color={group.expired ? 'var(--mantine-color-red-5)' : 'var(--mantine-color-turquoise-5)'}
          value={calculateProgress(group.creationTimestamp, group.expiryTimestamp, group.expired)}
        />
      </Tooltip>
      <Flex justify={'space-between'}>
        <Text size={'xs'}>{formatDate({ timestamp: group.creationTimestamp })}</Text>
        <Text size={'xs'}>{formatDate({ timestamp: group.expiryTimestamp })}</Text>
      </Flex>
    </Box>
  );
};
